import { ErrorAlert } from "@components/ErrorAlert";
import {
  toWSDateString,
  useWSModalCustom,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSForm,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useCallback } from "react";
import * as Yup from "yup";
import { PayeeEngagementType, WorkLog } from "@wingspanhq/wingspan-api-sdk";
import { queriesWorkLogs } from "@query";
import { PublicCommentField } from "../../components/PublicCommentField";
import { useQueryWorkLogPayrollRun } from "../../hooks/useQueryWorkLogPayrollRun";
import { selectorWorkLogConvertedResourceLabels } from "../../selectors/selectorWorkLogConvertedResourceLabels";
import { selectorWorkLogPayPeriodLabel } from "../../selectors/selectorWorkLogPayPeriodLabel";
import { selectorWorkLogWorkerNoun } from "../../selectors/selectorWorkLogWorkerNoun";

type FormData = {
  comment: string;
};

type Props = {
  workLog: WorkLog;
  contractorName?: string;
  /**
   * Names what rejecting prevents. The work log has not converted, so
   * `convertedResourceType` is null here and the engagement is the only signal.
   */
  engagementType?: PayeeEngagementType | null;
} & WSModalCustomProps<{
  success?: boolean;
}>;

export const ModalRejectWorkLog: React.FC<Props> = ({
  workLog,
  contractorName = "the contractor",
  engagementType,
  ...modalProps
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [rejectWorkLog, rejectMeta] =
    queriesWorkLogs.rejectWorkLog.useMutation();

  // What the payer is actually taking away by rejecting: this pay period's slot
  // in a specific payroll run, paying out on a specific date. Every part is
  // optional — a contractor work log has no pay period, and an unscheduled one
  // has no run — so the copy is assembled from whatever resolved.
  const { payrollRun } = useQueryWorkLogPayrollRun(workLog);
  const payPeriodLabel = selectorWorkLogPayPeriodLabel(workLog);
  const convertedResourceNoun = selectorWorkLogConvertedResourceLabels({
    convertedResourceType: workLog.convertedResourceType,
    engagementType
  }).noun;
  const workerNoun = selectorWorkLogWorkerNoun(engagementType);

  const handleReject = useCallback(
    async (formData: FormData) => {
      rejectWorkLog(
        {
          workLogId: workLog.id!,
          rejectWorkLogRequest: { reason: formData.comment }
        },
        {
          onSuccess: () => {
            modalProps.onClose({ success: true });
            openSnackbar({
              icon: { name: "check" },
              message: "Work log rejected"
            });
          },
          onError: () => {
            openSnackbar({
              type: "warning",
              message: "Failed to reject work log. Please try again."
            });
          }
        }
      );
    },
    [openSnackbar, modalProps, rejectWorkLog, workLog.id]
  );

  return (
    <WSModal title="Reject work log" {...modalProps}>
      <WSForm<FormData>
        onSubmit={handleReject}
        defaultValues={{ comment: "" }}
        validationSchema={Yup.object().shape({
          comment: Yup.string()
            .trim()
            .required("Please explain why the work log is being rejected")
        })}
      >
        <WSList gap="XL">
          <ErrorAlert role="alert" context="WorkLog" error={rejectMeta.error} />
          <WSText.ParagraphSm color="gray500">
            Are you sure you want to reject the work log?
          </WSText.ParagraphSm>

          <WSAlert
            theme="info"
            title="About rejected work logs"
            icon="info-circle"
          >
            {payPeriodLabel && (
              <>
                {"This work log is for pay period "}
                <WSText.ParagraphSm inline weight="medium">
                  {payPeriodLabel}
                </WSText.ParagraphSm>
                {payrollRun?.payrollNumber && (
                  <>
                    {" for payroll run "}
                    <WSText.ParagraphSm inline weight="medium">
                      #{payrollRun.payrollNumber}
                    </WSText.ParagraphSm>
                  </>
                )}
                {". "}
                {payrollRun?.payDate
                  ? `Rejecting it means it will not be paid on ${toWSDateString(
                      payrollRun.payDate,
                      "monthDayYear"
                    )} for this pay period. `
                  : "Rejecting it means it will not be paid for this pay period. "}
              </>
            )}
            {`Once a work log is rejected, it cannot be reverted and no ${convertedResourceNoun} is created. Please provide the necessary detail to your ${workerNoun} about the rejection.`}
          </WSAlert>

          {/* Required, per the design: a rejection the contractor can't explain
              is the one comment that must never be blank. */}
          <PublicCommentField
            label="Comment *"
            description={`Comment is visible to both you and the ${workerNoun}. Please provide specific details that will inform ${contractorName} about the decision.`}
            placeholder="Provide specific details about the decision"
          />

          <WSActions
            alignment="fill"
            buttons={[
              {
                kind: "Secondary",
                label: "No, go back",
                type: "button",
                onClick: () => {
                  modalProps.onClose({ success: false });
                }
              },
              {
                kind: "Primary",
                destructive: true,
                label: "Yes, reject work log",
                loading: rejectMeta.isLoading
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSModal>
  );
};

export const useModalRejectWorkLog = () => useWSModalCustom(ModalRejectWorkLog);
